import type { ReactNode } from 'react'
import {
  IconeAdicionar,
  IconeCena,
  IconeAlerta,
  IconeConcluido,
  IconeConstrucao,
  IconeCorda,
  IconeMosquetao,
  IconePolia,
  IconeDesfazer,
  IconeDestravado,
  IconeDuplicar,
  IconeEscalar,
  IconeEstadoClinico,
  IconeExportar,
  IconeFerramenta,
  IconeGirar,
  IconeMover,
  IconeGrade,
  IconePistaAsfalto,
  IconePonto,
  IconeRefazer,
  IconeRemover,
  IconeReproduzir,
  IconeRestaurar,
  IconeTravado,
} from '../icones'
import type { ConteudoInspetor, FerramentasModulo } from './tipos'

export type FerramentaGizmo = 'translate' | 'rotate' | 'scale'

export interface PropsTabletop {
  secaoAtiva?: 'cenario'
  aoAtivarSecao?: (secao: 'cenario') => void
  /** O painel do cenário: história da ocorrência e exemplos de salvamento para abrir. */
  painelRoteiroAula?: (secao: 'cenario') => ReactNode
  modoRoteiroAula?: boolean
  aoAlternarModoRoteiro?: () => void
  asfaltoAtivo?: boolean
  /** Mostra os pontos de ancoragem de todos os objetos; o selecionado sempre mostra os dele. */
  pontosAncoragemAtivos?: boolean
  aoAlternarPontosAncoragem?: () => void
  /** Setas de tração: mostra para onde cada corda puxa. */
  setasTracaoAtivas?: boolean
  aoAlternarSetasTracao?: () => void
  /** Equipamentos pequenos desenhados maiores, para a aula. */
  destaqueEquipamentosAtivo?: boolean
  aoAlternarDestaqueEquipamentos?: () => void
  /** A puxada animada: as cordas com seta correm e as roldanas giram. */
  animarPuxadaAtivo?: boolean
  aoAlternarAnimarPuxada?: () => void
  /** Testar sistema de içamento e redução de força animado. */
  aoTestarSistema?: () => void
  testeSistemaAtivo?: boolean
  aoAlternarAsfalto?: () => void
  /** Grade-régua do piso: o instrutor liga e desliga. */
  gradeAtiva?: boolean
  aoAlternarGrade?: () => void
  /** A grade está escondida pela própria cena (canal, cisterna ou chão de mato). */
  gradeOcultaPelaCena?: boolean
  /** Cenário travado: só equipamentos se movem no arraste; veículos e obstáculos ficam fixos. */
  cenarioTravado?: boolean
  aoAlternarTravaCenario?: () => void
  painelAdicionar: () => ReactNode
  /** Ferramenta de corda: função, desenho por pontos de ancoragem, lista e inspetor. */
  painelCordas?: () => ReactNode
  /** A corda selecionada (sem peça selecionada): vai para o inspetor, em vez de um card sobre a cena. */
  inspetorCorda?: ConteudoInspetor | null
  quantidadeCordas?: number
  /** Avisos didáticos da montagem: lista, com a contagem na aba. */
  painelAvisos?: () => ReactNode
  /** Checklist de trabalho em altura: só aparece com trabalhador na mesa. */
  painelChecklistAltura?: () => ReactNode
  /** Itens do checklist em aberto (pendentes ou que a montagem não atende). */
  quantidadeChecklistAltura?: number
  quantidadeAvisos?: number

  painelFerramentas: () => ReactNode
  painelFila: () => ReactNode
  painelObjetos: () => ReactNode
  painelCena: () => ReactNode
  painelIo: () => ReactNode
  painelEditor: () => ReactNode
  encaixeAtivo: boolean
  aoAlternarEncaixe: () => void
  quantidadeObjetos: number
  quantidadeFila: number
  ferramenta: FerramentaGizmo
  aoEscolherFerramenta: (ferramenta: FerramentaGizmo) => void
  desfazer: () => void
  refazer: () => void
  podeDesfazer: boolean
  podeRefazer: boolean
  objetoSelecionado: {
    id: string
    nome: string
    tipo: string
    posicao: readonly [number, number, number]
    rotacaoGraus: number
    escala: number
    oculto: boolean
    bloqueado: boolean
    aoDuplicar: () => void
    aoRestaurar: () => void
    aoRemover: () => void
  } | null
  aoLimparSelecao: () => void
}

const GIZMOS: ReadonlyArray<{ id: FerramentaGizmo; rotulo: string; Icone: typeof IconeMover }> = [
  { id: 'translate', rotulo: 'Mover', Icone: IconeMover },
  { id: 'rotate', rotulo: 'Girar', Icone: IconeGirar },
  { id: 'scale', rotulo: 'Escalar', Icone: IconeEscalar },
]

/**
 * Table top: abas independentes para cenário, vítima, corte e estabilização; ferramentas de
 * montagem livres acessíveis em modo de edição de maquete.
 */
export function ferramentasTabletop(p: PropsTabletop): FerramentasModulo {
  const objeto = p.objetoSelecionado

  // No celular, desfazer e refazer ficam sempre à mão, só com o ícone; o resto das ações
  // recolhe atrás de "Opções" para não cobrir a cena (os botões com texto, na lista, somem lá).
  const acoesFixasMobile = (
    <>
      <button
        type="button"
        className="btn-acao-cad btn-acao-cad--secundario btn-acao-cad--icone"
        disabled={!p.podeDesfazer}
        onClick={p.desfazer}
        aria-label="Desfazer"
        title="Desfazer (Ctrl+Z)"
      >
        <IconeDesfazer />
      </button>
      <button
        type="button"
        className="btn-acao-cad btn-acao-cad--secundario btn-acao-cad--icone"
        disabled={!p.podeRefazer}
        onClick={p.refazer}
        aria-label="Refazer"
        title="Refazer (Ctrl+Shift+Z)"
      >
        <IconeRefazer />
      </button>
    </>
  )

  // Ramo prioritário: Roteiro Didático de Aula
  if (p.painelRoteiroAula) {
    return {
      grupos: [
        { id: 'adicionar', rotulo: 'Adicionar', icone: <IconeAdicionar />, conteudo: p.painelAdicionar },
        ...(p.painelCordas ? [{ id: 'cordas', rotulo: 'Cordas', icone: <IconeCorda />, badge: p.quantidadeCordas, conteudo: p.painelCordas }] : []),
        ...(p.painelAvisos ? [{ id: 'avisos', rotulo: 'Avisos', icone: <IconeAlerta />, badge: p.quantidadeAvisos, conteudo: p.painelAvisos }] : []),
        ...(p.painelChecklistAltura ? [{ id: 'checklist-altura', rotulo: 'Checklist', icone: <IconeConcluido />, badge: p.quantidadeChecklistAltura, conteudo: p.painelChecklistAltura }] : []),
        { id: 'cenario', rotulo: 'Cenário', icone: <IconeCena />, conteudo: () => p.painelRoteiroAula!('cenario'), ativa: p.secaoAtiva === 'cenario', aoAtivar: () => p.aoAtivarSecao?.('cenario') },
        { id: 'editor', rotulo: 'Editar elemento', icone: <IconeConstrucao />, conteudo: p.painelEditor },
        {
          id: 'io',
          rotulo: 'Importar/Exportar',
          rotuloCurto: 'Arquivo',
          icone: <IconeExportar />,
          conteudo: () => <>{p.painelCena()}{p.painelIo()}</>,
        },
      ],
      chips: [
        { id: 'objetos', rotulo: 'Objetos', valor: p.quantidadeObjetos, tom: 'info' },
      ],
      acoesFixasMobile,
      rotuloOpcoesMobile: 'Opções da cena',
      acoesRapidas: (
        <>
          {p.aoAlternarAsfalto && (
            <button
              type="button"
              className={`btn-acao-cad ${p.asfaltoAtivo ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
              onClick={p.aoAlternarAsfalto}
              aria-pressed={p.asfaltoAtivo}
              title={p.asfaltoAtivo ? 'Ocultar pista de asfalto (mostrar apenas grid neutro)' : 'Exibir pista de asfalto com faixas e acostamento'}
            >
              <IconePistaAsfalto />{p.asfaltoAtivo ? 'Pista de Asfalto' : 'Grid Neutro'}
            </button>
          )}
          {p.aoAlternarGrade && (
            <button
              type="button"
              className={`btn-acao-cad ${p.gradeAtiva && !p.gradeOcultaPelaCena ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
              onClick={p.aoAlternarGrade}
              aria-pressed={Boolean(p.gradeAtiva && !p.gradeOcultaPelaCena)}
              disabled={p.gradeOcultaPelaCena}
              title={p.gradeOcultaPelaCena
                ? 'A grade fica oculta nesta cena: o chão não é plano (canal, cisterna ou mato)'
                : p.gradeAtiva ? 'Ocultar a grade do piso' : 'Mostrar a grade do piso (régua de meio em meio metro)'}
            >
              <IconeGrade />Grade
            </button>
          )}
          {p.aoAlternarPontosAncoragem && (
            <button
              type="button"
              className={`btn-acao-cad ${p.pontosAncoragemAtivos ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
              onClick={p.aoAlternarPontosAncoragem}
              aria-pressed={p.pontosAncoragemAtivos}
              title={p.pontosAncoragemAtivos
                ? 'Ocultar os pontos de ancoragem (o objeto selecionado continua mostrando os dele)'
                : 'Mostrar os pontos de ancoragem de todos os objetos'}
            >
              <IconePonto />Pontos de ancoragem
            </button>
          )}
          {p.aoAlternarSetasTracao && (
            <button
              type="button"
              className={`btn-acao-cad ${p.setasTracaoAtivas ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
              onClick={p.aoAlternarSetasTracao}
              aria-pressed={p.setasTracaoAtivas}
              title={p.setasTracaoAtivas
                ? 'Ocultar as setas de tração das cordas'
                : 'Mostrar para onde cada corda puxa'}
            >
              <IconeCorda />Setas de tração
            </button>
          )}
          {p.aoAlternarDestaqueEquipamentos && (
            <button
              type="button"
              className={`btn-acao-cad ${p.destaqueEquipamentosAtivo ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
              onClick={p.aoAlternarDestaqueEquipamentos}
              aria-pressed={p.destaqueEquipamentosAtivo}
              title="Desenhar mosquetões, polias, placas e nós maiores, para enxergar na aula (os pontos acompanham)"
            >
              <IconeMosquetao />Destacar equipamentos
            </button>
          )}
          {p.aoAlternarAnimarPuxada && (
            <button
              type="button"
              className={`btn-acao-cad ${p.animarPuxadaAtivo ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
              onClick={p.aoAlternarAnimarPuxada}
              aria-pressed={p.animarPuxadaAtivo}
              title="Animar a puxada: as cordas com seta correm e as roldanas por onde passam giram"
            >
              <IconePolia />Animar puxada
            </button>
          )}
          {p.aoTestarSistema && (
            <button
              type="button"
              className={`btn-acao-cad ${p.testeSistemaAtivo ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
              onClick={p.aoTestarSistema}
              aria-pressed={p.testeSistemaAtivo}
              title="Testar o sistema montado: simular a puxada da corda, compressão do sistema e subida da carga com cálculo de forças"
            >
              <IconeReproduzir />Testar Sistema
            </button>
          )}
          {p.aoAlternarTravaCenario && (
            <button
              type="button"
              className={`btn-acao-cad ${p.cenarioTravado ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
              onClick={p.aoAlternarTravaCenario}
              aria-pressed={p.cenarioTravado}
              title={p.cenarioTravado
                ? 'Cenário travado: veículos e obstáculos fixos. Clique para destravar.'
                : 'Travar cenário: fixa veículos e obstáculos; só os equipamentos se movem.'}
            >
              {p.cenarioTravado ? <><IconeTravado />Cenário Travado</> : <><IconeDestravado />Travar Cenário</>}
            </button>
          )}
          <button
            type="button"
            className="btn-acao-cad btn-acao-cad--secundario tt-historico"
            disabled={!p.podeDesfazer}
            onClick={p.desfazer}
            title="Desfazer (Ctrl+Z)"
          >
            <IconeDesfazer />Desfazer
          </button>
          <button
            type="button"
            className="btn-acao-cad btn-acao-cad--secundario tt-historico"
            disabled={!p.podeRefazer}
            onClick={p.refazer}
            title="Refazer (Ctrl+Shift+Z)"
          >
            <IconeRefazer />Refazer
          </button>
        </>
      ),
      inspetor: null,
    }
  }

  const botoesGizmo = (incluirEscala: boolean) =>
    GIZMOS.filter((g) => incluirEscala || g.id !== 'scale').map(({ id, rotulo, Icone }) => (
      <button
        key={id}
        type="button"
        className={`btn-acao-cad ${p.ferramenta === id ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
        onClick={() => p.aoEscolherFerramenta(id)}
        aria-pressed={p.ferramenta === id}
        title={`${rotulo} objeto`}
      >
        <Icone />{rotulo}
      </button>
    ))

  const gruposCompletos = [
    { id: 'adicionar', rotulo: 'Adicionar', rotuloCurto: 'Novo', icone: <IconeAdicionar />, conteudo: p.painelAdicionar },
    ...(p.painelCordas ? [{ id: 'cordas', rotulo: 'Cordas', icone: <IconeCorda />, badge: p.quantidadeCordas, conteudo: p.painelCordas }] : []),
    ...(p.painelAvisos ? [{ id: 'avisos', rotulo: 'Avisos', icone: <IconeAlerta />, badge: p.quantidadeAvisos, conteudo: p.painelAvisos }] : []),
    ...(p.painelChecklistAltura ? [{ id: 'checklist-altura', rotulo: 'Checklist', icone: <IconeConcluido />, badge: p.quantidadeChecklistAltura, conteudo: p.painelChecklistAltura }] : []),
    {
      id: 'ferramentas',
      rotulo: 'Ferramentas',
      icone: <IconeFerramenta />,
      conteudo: p.painelFerramentas,
      // só tem Desfazer/Refazer, que no celular já flutuam sobre a cena
      somenteDesktop: true,
    },
    {
      id: 'objetos',
      rotulo: 'Objetos',
      icone: <IconeConstrucao />,
      badge: p.quantidadeObjetos,
      conteudo: p.painelObjetos,
    },
    { id: 'cena', rotulo: 'Cena', icone: <IconeCena />, conteudo: p.painelCena },
    {
      id: 'io',
      rotulo: 'Importar/Exportar',
      rotuloCurto: 'Arquivo',
      icone: <IconeExportar />,
      conteudo: p.painelIo,
    },
    {
      id: 'editor',
      rotulo: 'Editor',
      icone: <IconeConstrucao />,
      desabilitado: !objeto,
      conteudo: p.painelEditor,
    },
  ]

  const grupos = gruposCompletos

  return {
    grupos,
    chips: [
      { id: 'objetos', rotulo: 'Objetos', valor: p.quantidadeObjetos, tom: 'info' },
    ],
    acoesFixasMobile,
    rotuloOpcoesMobile: 'Opções da cena',
    acoesRapidas: (
      <>
        {p.aoAlternarModoRoteiro && (
          <button
            type="button"
            className="btn-acao-cad btn-acao-cad--destaque"
            onClick={p.aoAlternarModoRoteiro}
            title="Voltar às abas da Aula"
          >
            <IconeCena />Voltar ao Roteiro
          </button>
        )}
        <>
            {botoesGizmo(false)}
            <button type="button"
              className={`btn-acao-cad ${p.encaixeAtivo ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
              aria-pressed={p.encaixeAtivo}
              onClick={p.aoAlternarEncaixe}
              title="Alinhar posições a cada 25 cm">
              Encaixe 25 cm
            </button>
        </>
        {p.aoAlternarPontosAncoragem && (
          <button
            type="button"
            className={`btn-acao-cad ${p.pontosAncoragemAtivos ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
            onClick={p.aoAlternarPontosAncoragem}
            aria-pressed={p.pontosAncoragemAtivos}
            title={p.pontosAncoragemAtivos
              ? 'Ocultar os pontos de ancoragem (o objeto selecionado continua mostrando os dele)'
              : 'Mostrar os pontos de ancoragem de todos os objetos'}
          >
            <IconePonto />Pontos de ancoragem
          </button>
        )}
        {p.aoAlternarSetasTracao && (
          <button
            type="button"
            className={`btn-acao-cad ${p.setasTracaoAtivas ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
            onClick={p.aoAlternarSetasTracao}
            aria-pressed={p.setasTracaoAtivas}
            title={p.setasTracaoAtivas
              ? 'Ocultar as setas de tração das cordas'
              : 'Mostrar para onde cada corda puxa'}
          >
            <IconeCorda />Setas de tração
          </button>
        )}
        {p.aoAlternarDestaqueEquipamentos && (
          <button
            type="button"
            className={`btn-acao-cad ${p.destaqueEquipamentosAtivo ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
            onClick={p.aoAlternarDestaqueEquipamentos}
            aria-pressed={p.destaqueEquipamentosAtivo}
            title="Desenhar mosquetões, polias, placas e nós maiores, para enxergar na aula (os pontos acompanham)"
          >
            <IconeMosquetao />Destacar equipamentos
          </button>
        )}
        {p.aoAlternarAnimarPuxada && (
          <button
            type="button"
            className={`btn-acao-cad ${p.animarPuxadaAtivo ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
            onClick={p.aoAlternarAnimarPuxada}
            aria-pressed={p.animarPuxadaAtivo}
            title="Animar a puxada: as cordas com seta correm e as roldanas por onde passam giram"
          >
            <IconePolia />Animar puxada
          </button>
        )}
        {p.aoTestarSistema && (
          <button
            type="button"
            className={`btn-acao-cad ${p.testeSistemaAtivo ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
            onClick={p.aoTestarSistema}
            aria-pressed={p.testeSistemaAtivo}
            title="Testar o sistema montado: simular a puxada da corda, compressão do sistema e subida da carga com cálculo de forças"
          >
            <IconeReproduzir />Testar Sistema
          </button>
        )}
        {p.aoAlternarAsfalto && (
          <button
            type="button"
            className={`btn-acao-cad ${p.asfaltoAtivo ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
            onClick={p.aoAlternarAsfalto}
            aria-pressed={p.asfaltoAtivo}
            title={p.asfaltoAtivo ? 'Ocultar pista de asfalto (mostrar apenas grid neutro)' : 'Exibir pista de asfalto com faixas e acostamento'}
          >
            <IconePistaAsfalto />{p.asfaltoAtivo ? 'Pista de Asfalto' : 'Grid Neutro'}
          </button>
        )}
        {p.aoAlternarGrade && (
          <button
            type="button"
            className={`btn-acao-cad ${p.gradeAtiva && !p.gradeOcultaPelaCena ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
            onClick={p.aoAlternarGrade}
            aria-pressed={Boolean(p.gradeAtiva && !p.gradeOcultaPelaCena)}
            disabled={p.gradeOcultaPelaCena}
            title={p.gradeOcultaPelaCena
              ? 'A grade fica oculta nesta cena: o chão não é plano (canal, cisterna ou mato)'
              : p.gradeAtiva ? 'Ocultar a grade do piso' : 'Mostrar a grade do piso (régua de meio em meio metro)'}
          >
            <IconeGrade />Grade
          </button>
        )}
        <button
          type="button"
          className="btn-acao-cad btn-acao-cad--secundario tt-historico"
          disabled={!p.podeDesfazer}
          onClick={p.desfazer}
          title="Desfazer (Ctrl+Z)"
        >
          <IconeDesfazer />Desfazer
        </button>
        <button
          type="button"
          className="btn-acao-cad btn-acao-cad--secundario tt-historico"
          disabled={!p.podeRefazer}
          onClick={p.refazer}
          title="Refazer (Ctrl+Shift+Z)"
        >
          <IconeRefazer />Refazer
        </button>
      </>
    ),
    // Propriedades/gizmo de edição não fazem sentido com a cena travada em Atender.
    inspetor: (objeto && {
      titulo: objeto.nome,
      subtitulo: objeto.tipo,
      propriedades: [
        {
          rotulo: 'Posição (m)',
          valor: objeto.posicao.map((n) => n.toFixed(2)).join(' · '),
        },
        { rotulo: 'Rotação', valor: `${objeto.rotacaoGraus}°` },
        { rotulo: 'Escala', valor: `${objeto.escala.toFixed(2)}×` },
        { rotulo: 'Visibilidade', valor: objeto.oculto ? 'oculto' : 'visível' },
        { rotulo: 'Bloqueio', valor: objeto.bloqueado ? 'travado' : 'livre' },
      ],
      acoes: (
        <>
          <div className="inspetor__linha-controle">{botoesGizmo(true)}</div>
          <button
            type="button"
            className="btn-acao-cad btn-acao-cad--secundario"
            onClick={objeto.aoDuplicar}
          >
            <IconeDuplicar />Duplicar
          </button>
          <button
            type="button"
            className="btn-acao-cad btn-acao-cad--secundario"
            onClick={objeto.aoRestaurar}
          >
            <IconeRestaurar />Restaurar transformação
          </button>
          <button
            type="button"
            className="btn-acao-cad btn-acao-cad--perigo"
            onClick={objeto.aoRemover}
          >
            <IconeRemover />Remover
          </button>
        </>
      ),
      aoFechar: p.aoLimparSelecao,
    }) || (p.inspetorCorda ?? null),
  }
}

